import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { TableQueryFilters } from "@/pages/datasets/types";
import dayjs from "dayjs";

interface UseTableQueriesFiltersDefaults {
  sortAttribute?: string;
  sortOrder?: string;
}

export function useTableQueriesFilters(defaults?: UseTableQueriesFiltersDefaults) {
  const [searchParams] = useSearchParams();
  const sortAttribute = searchParams.get("sortAttribute") || defaults?.sortAttribute || "";
  const sortOrder = searchParams.get("sortOrder") || defaults?.sortOrder || "";

  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();

  // Get date filters from context
  const startDate = useMemo(
    () => getFilterValue<Date>("startDate") || dayjs().subtract(28, "day").toDate(),
    [getFilterValue]
  );
  const endDate = useMemo(
    () => getFilterValue<Date>("endDate") || dayjs().toDate(),
    [getFilterValue]
  );

  // Warehouse-specific filter getters
  const insightFilter = useMemo(() => getFilterValue<string[]>("insights") || [], [getFilterValue]);
  const estimatedCostFilter = useMemo(
    () => getFilterValue<string>("estimatedCost"),
    [getFilterValue]
  );
  const estimatedCostOperatorFilter = useMemo(
    () => getFilterValue<string>("estimatedCostOperator"),
    [getFilterValue]
  );
  const executionTimeFilter = useMemo(
    () => getFilterValue<string>("executionTime"),
    [getFilterValue]
  );
  const executionTimeOperatorFilter = useMemo(
    () => getFilterValue<string>("executionTimeOperator"),
    [getFilterValue]
  );
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouse") || [],
    [getFilterValue]
  );
  const userFilter = useMemo(() => getFilterValue<string[]>("user") || [], [getFilterValue]);
  const instanceFilter = useMemo(
    () => getFilterValue<string[]>("instance") || [],
    [getFilterValue]
  );
  const accessTypeFilter = useMemo(
    () => getFilterValue<string[]>("accessType") || [],
    [getFilterValue]
  );

  // Convert filters to API parameters
  const filterParams = useMemo((): TableQueryFilters => {
    const params: TableQueryFilters = {
      page: (page + 1).toString(10),
      size: pageSize.toString(10),
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
    };

    if (sortAttribute) {
      params.order_by = sortAttribute;
      params.sort_order = sortOrder;
    }

    if (warehouseFilter.length > 0) params.warehouse_names = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    if (insightFilter.length > 0) params.insights = insightFilter;
    if (userFilter.length > 0) params.user_names = userFilter;
    if (accessTypeFilter.length > 0) params.access_types = accessTypeFilter;
    if (estimatedCostFilter !== undefined && estimatedCostFilter !== "") {
      params.estimated_cost_filter = estimatedCostFilter;
    }
    if (estimatedCostOperatorFilter !== undefined && estimatedCostOperatorFilter !== "") {
      params.estimated_cost_filter_mode = estimatedCostOperatorFilter;
    }
    if (executionTimeFilter !== undefined && executionTimeFilter !== "") {
      params.execution_time_filter = executionTimeFilter;
    }
    if (executionTimeOperatorFilter !== undefined && executionTimeOperatorFilter !== "") {
      params.execution_time_filter_mode = executionTimeOperatorFilter;
    }

    return params;
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    sortAttribute,
    sortOrder,
    warehouseFilter,
    instanceFilter,
    insightFilter,
    userFilter,
    accessTypeFilter,
    estimatedCostFilter,
    estimatedCostOperatorFilter,
    executionTimeFilter,
    executionTimeOperatorFilter,
  ]);

  return {
    filterParams,
    isInitialized,
  };
}
